import { Box, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { PersonAvatar } from '../people';
import type { SettingsSample } from '../sampleData';
import type { DetailsValues } from '../state';

import { DetailsCard, FactGrid, FactLabel } from './DetailsCard';
import type { CardEditProps } from './DetailsCard';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * Who's involved (brief §3): project manager, account manager, partner and
 * contact, each with their initials, then the customer — read-only, locked,
 * because it was set when the project was created.
 */
export type PeopleCardProps = {
  values: Pick<DetailsValues, 'projectManagerId' | 'accountManagerId' | 'partnerId' | 'contactId'>;
  sample: SettingsSample;
  /** The customer's contacts, for the contact's name. */
  contacts: { id: string; name: string }[];
  customerName: string | undefined;
  /** Edit mode, for a card that has its form. */
  edit?: CardEditProps;
};

export const PeopleCard = ({ values, sample, contacts, customerName, edit }: PeopleCardProps) => {
  const { t } = useTranslation('pmDetails');
  const employee = (id: string) => sample.employees.find((person) => person.id === id)?.name;
  const rows = [
    {
      key: 'projectManager',
      label: t('people.ProjectManager'),
      name: employee(values.projectManagerId),
    },
    {
      key: 'accountManager',
      label: t('people.AccountManager'),
      name: employee(values.accountManagerId),
    },
    { key: 'partner', label: t('people.Partner'), name: employee(values.partnerId) },
    {
      key: 'contact',
      label: t('people.Contact'),
      name: contacts.find((contact) => contact.id === values.contactId)?.name,
    },
  ];
  return (
    <DetailsCard
      icon="group"
      title={t('people.Title')}
      automationId="details-card-people"
      {...edit}
    >
      <FactGrid>
        {rows.map((row) => (
          <Fragment key={row.key}>
            <FactLabel>{row.label}</FactLabel>
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}
              data-automation-id={`details-people-${row.key}`}
            >
              {row.name ? (
                <>
                  <PersonAvatar name={row.name} />
                  <Typography variant="bodyRegular" component="span">
                    {row.name}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="bodyRegular"
                  component="span"
                  sx={{ color: 'var(--color-fog-80)' }}
                >
                  {t('people.NotSet')}
                </Typography>
              )}
            </Stack>
          </Fragment>
        ))}
        <FactLabel>{t('people.Customer')}</FactLabel>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 0.75, minWidth: 0 }}
          data-automation-id="details-people-customer"
        >
          <MaterialSymbol name="corporate_fare" size={18} color="var(--color-fog-80)" />
          <Typography variant="bodyRegular" component="span">
            {customerName ?? '—'}
          </Typography>
          <Box
            component="span"
            title={t('people.CustomerLocked')}
            aria-label={t('people.CustomerLocked')}
            role="img"
            sx={{ display: 'inline-flex' }}
          >
            <MaterialSymbol name="lock" size={16} color="var(--color-fog-80)" />
          </Box>
        </Stack>
      </FactGrid>
    </DetailsCard>
  );
};
